import React, { useEffect, useState } from 'react';
import {
    AspectRatio,
    Box,
    Button,
    FlexBox,
    ResponsiveImage,
    StandardTileImage,
    Typography,
    tokensRaw,
} from '@vp/swan';
import { bugtracker } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { useBriefContext } from '../../../../BriefContext';
import { useViewContext } from '../../../ViewContext';
import { Field_ClientConfiguredField_Fragment } from '../../../brief.generated';
import { valueToWorkIds } from './ReferenceProjectsInput';
import { WorkEntityViewsModal } from './WorkEntityViewsModal';
import { ReferenceProjectsQuery, useReferenceProjectsQuery } from './referenceProjects.generated';

export type WorkEntity = ReferenceProjectsQuery['briefReferenceProjects'][0];

export function Review({ clientConfiguredValue }: Field_ClientConfiguredField_Fragment) {
    const { correlationId } = useBriefContext();
    const workIds = valueToWorkIds(clientConfiguredValue);
    const [isViewsModalOpen, setIsViewsModalOpen] = useState(false);
    const [selectedWorkEntity, setSelectedWorkEntity] = useState<WorkEntity | null>(null);
    const { isCollaborationMode } = useViewContext();

    const { loading, error, data, refetch } = useReferenceProjectsQuery({
        variables: {
            correlationId,
            // In collaboration mode, correlationId is a valid collaborationId (matching has happened)
            // In matching/brief review mode, there's no collaboration created yet
            collaborationId: isCollaborationMode ? correlationId : '',
        },
    });

    const selectedWorkEntities = data?.briefReferenceProjects || [];

    useEffect(() => {
        if (error) {
            bugtracker.notify(
                new Error(
                    'Error loading work entities for ReferenceProjectsReview with work IDs: ' +
                        clientConfiguredValue +
                        '. ' +
                        error
                )
            );
            return;
        }
    }, [clientConfiguredValue, error]);

    // refetch reference projects when workIds change. Refetch does not read from cache by default.
    useEffect(() => {
        refetch();
    }, [clientConfiguredValue, refetch]);

    return (
        <>
            <Box>
                <FlexBox gap={3}>
                    {loading
                        ? skeletonImages(workIds.length)
                        : selectedWorkEntities.map((workEntity) =>
                              // If no views are available, show a skeleton image. Replace with a better placeholder image in the future if needed.
                              workEntity.views.length === 0 ? (
                                  <SkeletonImage />
                              ) : (
                                  <Box
                                      key={workEntity.workId}
                                      style={{
                                          width: '48px',
                                      }}
                                  >
                                      <ImageWrapper
                                          onClick={
                                              isCollaborationMode
                                                  ? () => {
                                                        setIsViewsModalOpen(true);
                                                        setSelectedWorkEntity(workEntity);
                                                    }
                                                  : undefined
                                          }
                                          workName={workEntity.workName}
                                      >
                                          <ResponsiveImage
                                              key={workEntity.workId}
                                              style={{
                                                  height: '48px',
                                                  width: '48px',
                                                  borderRadius: tokensRaw.SwanSemBorderRadiusSubtle,
                                              }}
                                              src={workEntity.views[0]?._links.image.href}
                                              alt={workEntity.views[0]?._links.image.title}
                                          />
                                      </ImageWrapper>
                                      <Typography
                                          fontSkin={'footnote'}
                                          style={{
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                          }}
                                      >
                                          {workEntity.workName}
                                      </Typography>
                                  </Box>
                              )
                          )}
                </FlexBox>
            </Box>
            {selectedWorkEntity && (
                <WorkEntityViewsModal
                    isModalOpen={isViewsModalOpen}
                    setModalOpen={setIsViewsModalOpen}
                    workEntity={selectedWorkEntity}
                />
            )}
        </>
    );
}

// Wraps Image in a button if onClick is provided
const ImageWrapper = ({
    onClick,
    children,
    workName,
}: {
    onClick: (() => void) | undefined;
    children: React.ReactNode;
    workName: string | undefined;
}) =>
    onClick ? (
        <Button
            skin="unstyled"
            onClick={onClick}
            style={{ height: '48px' }}
            aria-label={
                workName ??
                __('Reference project views', {
                    _context:
                        'Fallback aria-label for the button that opens the reference project views modal when the project name is unavailable',
                })
            }
        >
            {children}
        </Button>
    ) : (
        children
    );

function SkeletonImage() {
    return (
        <StandardTileImage>
            <AspectRatio
                style={{
                    height: '48px',
                    width: '48px',
                    borderRadius: tokensRaw.SwanSemBorderRadiusSubtle,
                }}
                ratio={1}
                loadingShimmer
            />
        </StandardTileImage>
    );
}

function skeletonImages(numberOfImages: number) {
    return Array.from({ length: numberOfImages }).map(() => <SkeletonImage />);
}
